<template>
    <div class="main-section" v-if="details">
        <div class="content">
            <div class="breadcrumbs">
                <router-link to="/admin/users/">Clients</router-link>
                <template v-if="clientId">
                    <img src="@/assets/arrow-opener.svg" alt="arrow-right" class="p-10" />
                    <router-link :to="{ name: 'bo_user', params: { id: clientId } }">Client</router-link>
                </template>
                <img src="@/assets/arrow-opener.svg" alt="arrow-right" class="p-10" />
                {{ user.username }}
            </div>
            <div class="user-name">{{ user.username }} <span class="muted">{{ user.email }}</span></div>

            <div class="tab-holder">
                <div v-for="tab in tabs" :key="tab.key" class="tab"
                    :class="{ active: tabState == tab.key }" @click="tabState = tab.key">{{ tab.label }}</div>
            </div>

            <!-- General -->
            <template v-if="tabState == 'general'">
                <div class="card">
                    <div class="header"><div class="title">Personal details</div></div>
                    <div class="info-grid">
                        <div class="info-section" v-for="item in infoItems" :key="item.name">
                            <div class="name">{{ item.name }}</div>
                            <div class="value">{{ item.value }}</div>
                        </div>
                    </div>
                </div>

                <div class="card">
                    <div class="header">
                        <div class="title">Security</div>
                        <div class="flex gap-10">
                            <button class="common-button secondary" :disabled="busy" @click="confirmAction = 'password'">Send password reset email</button>
                            <button class="common-button secondary" :disabled="busy" @click="confirmAction = '2fa'">Reset 2FA</button>
                        </div>
                    </div>
                    <div class="info-grid">
                        <div class="info-section">
                            <div class="name">2FA required</div>
                            <Toggle
                                :value="user.two_fa_required ? 'on' : 'off'"
                                :options="[{ label: 'On', value: 'on' }, { label: 'Off', value: 'off' }]"
                                :disabled="busy"
                                @change="setTwoFARequired"
                            />
                        </div>
                        <div class="info-section">
                            <div class="name">2FA enrollment</div>
                            <div class="value">{{ user.two_factor_setup_completed ? 'Enrolled' : 'Not enrolled' }}</div>
                        </div>
                        <div class="info-section">
                            <div class="name">Password change required</div>
                            <div class="value">{{ user.password_reset_needed ? 'Yes' : 'No' }}</div>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Permissions -->
            <div class="card" v-if="tabState == 'permissions'">
                <RoleManagement :user="user" :adminMode="true" />
            </div>

            <!-- Login history -->
            <AdminTable v-if="tabState == 'logins'"
                class="w-full"
                :data="loginRows"
                header="Login history (last 20)"
                :headers="loginHeaders"
            ></AdminTable>

            <!-- IAM activity -->
            <AdminTable v-if="tabState == 'activity'"
                class="w-full"
                :data="activityRows"
                header="Permission & security changes (last 50)"
                :headers="activityHeaders"
            ></AdminTable>
        </div>

        <PopupCover v-if="confirmAction" @close="confirmAction = null">
            <ConfirmationPopup
                :title="confirmAction == '2fa' ? 'Reset 2FA' : 'Send password reset email'"
                :message="confirmAction == '2fa'
                    ? `${user.username} will have to set up 2FA again on the next login. Continue?`
                    : `A password reset link will be emailed to ${user.email}. Continue?`"
                @confirm="runConfirmedAction"
                @cancel="confirmAction = null"
            />
        </PopupCover>
    </div>
    <div v-else class="p-20">LOADING...</div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import dayjs from 'dayjs';
import BO_API from '@/api/bo_api.js';
import { getUserType } from '@/utils/iam_utils';
import AdminTable from '@/components/AdminTable.vue';
import RoleManagement from '@/components/RoleManagement.vue';
import PopupCover from '@/components/PopupCover.vue';
import ConfirmationPopup from '@/components/ConfirmationPopup.vue';
import Toggle from '@/elements/Toggle.vue';

const props = defineProps({ user_id: { type: String, required: true } });
const route = useRoute();
const store = useStore();

const clientId = route.query.client || null;
const details = ref(null);
const tabState = ref('general');
const confirmAction = ref(null);
const busy = ref(false);

const tabs = [
    { key: 'general', label: 'General' },
    { key: 'permissions', label: 'Permissions' },
    { key: 'logins', label: 'Login history' },
    { key: 'activity', label: 'Activity log' },
];

const user = computed(() => details.value.user);
const formatDate = (d) => d ? dayjs(d).format('YYYY-MM-DD HH:mm') : '-';

const infoItems = computed(() => [
    { name: 'Username', value: user.value.username },
    { name: 'Email', value: user.value.email },
    { name: 'Email verified', value: user.value.email_verified ? 'Yes' : 'No' },
    { name: 'Type', value: getUserType(user.value.type) || user.value.type || '-' },
    { name: 'IAM roles', value: user.value.roles.length ? user.value.roles.join(', ') : 'None' },
    { name: 'Account ID', value: user.value.account_id },
    { name: 'Created', value: formatDate(user.value.created) },
    { name: 'Last login', value: formatDate(user.value.lastLogin) },
]);

const loginHeaders = [
    { name: 'Date', key: 'date' },
    { name: 'IP', key: 'ip' },
    { name: 'User agent', key: 'userAgent' },
];
const loginRows = computed(() => details.value.loginHistory.map(h => ({ ...h, date: formatDate(h.loginDate) })));

const activityHeaders = [
    { name: 'Date', key: 'date' },
    { name: 'Action', key: 'action' },
    { name: 'Description', key: 'description' },
    { name: 'By', key: 'update_username' },
];
const activityRows = computed(() => details.value.iamLog.map(l => ({ ...l, date: formatDate(l.created_at) })));

async function load() {
    const response = await BO_API.getCompanyUserDetails(props.user_id);
    if (response) {
        details.value = response;
        document.title = 'Admin · ' + response.user.username;
    }
}

function notify(text) {
    store.dispatch('addNotification', { text, type: 'success' });
}

async function runConfirmedAction() {
    const action = confirmAction.value;
    confirmAction.value = null;
    busy.value = true;
    try {
        if (action == '2fa') {
            if (await BO_API.resetCompanyUserTwoFA(props.user_id)) notify('2FA has been reset');
        } else {
            if (await BO_API.sendCompanyUserPasswordReset(props.user_id)) notify('Password reset email sent');
        }
        await load();
    } finally {
        busy.value = false;
    }
}

async function setTwoFARequired(value) {
    busy.value = true;
    try {
        const response = await BO_API.setCompanyUserTwoFARequired(props.user_id, value == 'on');
        if (response) notify(`2FA requirement ${value == 'on' ? 'enabled' : 'disabled'}`);
        await load();
    } finally {
        busy.value = false;
    }
}

onMounted(load);
</script>

<style lang="scss" scoped>
.main-section {
    margin: 0 20px;
    padding: 20px 0;
}
.content {
    margin: auto;
    max-width: 1000px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
    text-align: left;
}
.breadcrumbs {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: bold;
    a {
        color: var(--primary-color);
        text-decoration: none;
    }
}
.user-name {
    font-size: 28px;
    font-weight: 600;
    .muted {
        font-size: 16px;
        font-weight: 400;
        color: rgb(116 119 127);
        margin-left: 8px;
    }
}
.tab-holder {
    display: flex;
    overflow: auto;
    font-size: 14px;
    border-bottom: 1px solid var(--border-color);
    .tab {
        white-space: nowrap;
        padding: 10px 20px;
        cursor: pointer;
        font-weight: 600;
        &:first-child { padding-left: 0; }
        &.active {
            color: var(--primary-color);
            border-bottom: 4px solid var(--primary-color);
        }
    }
}
.card {
    background: white;
    border: 1px solid var(--border-color);
    border-radius: 10px;
    overflow: hidden;
    .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        flex-wrap: wrap;
        min-height: 64px;
        padding: 10px 1.5rem;
        box-sizing: border-box;
        border-bottom: 1px solid var(--border-color);
    }
    .title {
        font-size: 1.25rem;
        font-weight: 700;
        color: black;
    }
}
.info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 20px;
    padding: 20px 1.5rem;
}
.info-section {
    line-height: 18px;
    .name {
        font-size: 14px;
        color: rgb(116 119 127);
        margin-bottom: 4px;
    }
    .value {
        font-size: 16px;
        color: black;
        font-weight: bold;
        word-break: break-all;
    }
}
</style>
